import { useEffect, useState } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { useQuery } from '@apollo/client';
import { subDays } from 'date-fns';
import { nl } from 'date-fns/locale';
import { format } from 'date-fns-tz';
import { debounce } from 'throttle-debounce';
import { MissedStyle, MissedListWrapperStyle, MissedPickerStyle } from 'components/Missed/Missed.style';
import ErrorMessage from 'components/ErrorMessage';
import ErrorBoundary from 'components/ErrorBoundary';
import { SkeletonMissedSection } from 'components/SkeletonElement/SkeletonElement';
import CommonFallback from 'components/CommonFallback';
import VideoList from 'components/VideoList';
import Picker from 'components/Picker';
import VideoModel from 'models/video.model';
import ProgramsByDateQuery from 'queries/programs/programsByDate.query';
import { isServer } from 'lib/isBrowser';
import { trackClick } from 'lib/tealium/provider';
import { IWithInitialProps } from 'typings/IWithInitialProps';
import EditorialOverviewQuery from 'queries/overview/overview.query';
import { IGetInitialProps } from 'typings/IGetInitialProps';
import OverviewMetadataQuery from 'queries/overview/overviewMetadata.query';
import { Heading2 } from 'components/Text/Text.style';
import EpisodeModel from 'models/episode.program.model';
import { filterVideosByUniqueSeriesGuid } from 'lib/helpers';
import {
  PROGRAM,
  FALLBACK_UI_LABELS,
  PROMO_QUERY_CLIENT,
  BREAKPOINTS,
  FEATURE_SLUG,
  FIXED_COUNT,
  META_DESCRIPTION_CONTENT,
  META_TITLE_CONTENT,
  TEALIUM_EVENT_NAME,
  TEALIUM_EVENT_CATEGORY,
  PROGRAM_SORT_KEY
} from '../../src/client/constants';
import { Grid, GridElement } from '../../src/client/styles/grid';
import useFeature from '../../src/client/hooks/useFeature';

const SKELETON_NUMBER_OF_POPULAR_ITEMS = 4;
const SKELETON_NUMBER_OF_OTHER_ITEMS = 20;
const MISSED_SLUG = 'gemist';

const capitalizeFirstLetter = (str = ''): string => str[0].toUpperCase() + str.slice(1);

const getMaxAmount = (): number => {
  if (typeof window === 'undefined') return 4;
  if (window.innerWidth >= BREAKPOINTS.mdMin && window.innerWidth <= BREAKPOINTS.mdMax) return 3;
  return 4;
};

const Missed: IWithInitialProps<{
  initialMetadata?: IOverviewMetadata;
}> = props => {
  const [dayToDisplay, setDayToDisplay] = useState(1);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const [maxAmount, setMaxAmount] = useState(() => getMaxAmount());
  const [metadata, setMetadata] = useState<IOverviewMetadata | null>(props.initialMetadata || null);
  const [programs, setPrograms] = useState<VideoModel[]>([]);
  const [sortedPrograms, setSortedPrograms] = useState<{ favorites?: VideoModel[]; sorted: VideoModel[] }>();

  const [SelectOptions] = useState<
    { label: string; moreTitle: string; weekDay: string; popularTitle: string; date: string }[]
  >(() => {
    const today = new Date();
    return Array(8)
      .fill(0)
      .map((_, index) => {
        const day = subDays(today, index);
        const date = format(day, 'yyyy-MM-dd');

        let weekDay = capitalizeFirstLetter(
          format(day, 'EEEE', {
            locale: nl
          })
        );
        const weekDayShort = capitalizeFirstLetter(format(day, 'EEE', { locale: nl })).slice(0, 2);
        if (index === 0) weekDay = 'Vandaag';
        if (index === 1) weekDay = 'Gisteren';

        return {
          label: index < 2 ? weekDay : `${weekDayShort} ${format(day, 'dd MMM', { locale: nl }).replace('.', '')}`,
          popularTitle: `${weekDay} populair`,
          moreTitle: `Meer van ${weekDay.toLowerCase()}`,
          weekDay,
          date
        };
      });
  });
  const { popularTitle, date, moreTitle, label } = SelectOptions[dayToDisplay];

  useEffect(() => {
    let ScrollPosition = window.scrollY;
    const handleScroll = (): void => {
      setIsHeaderHidden(window.scrollY > 80 && window.scrollY >= ScrollPosition);
      ScrollPosition = window.scrollY;
    };

    const handleResize = (): void => {
      setMaxAmount(getMaxAmount());
    };

    window.addEventListener('scroll', handleScroll);

    const debouncedHandleResize = debounce(FIXED_COUNT.DEBOUNCE_TIME, handleResize);
    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, []);

  useQuery<{ overview: { metadata?: IOverviewMetadata } | null }>(EditorialOverviewQuery, {
    variables: {
      slug: MISSED_SLUG,
      client: PROMO_QUERY_CLIENT.DESKTOP
    },
    skip: !!metadata,
    onCompleted: data => {
      if (data.overview?.metadata) {
        setMetadata(data.overview.metadata);
      }
    }
  });

  const handleDayChange = (day: string): void => {
    const Index: number = SelectOptions.findIndex(d => d.label === day);
    if (dayToDisplay === Index) return;
    trackClick({
      name: TEALIUM_EVENT_NAME.MENU_CLICK,
      category: TEALIUM_EVENT_CATEGORY.MENU,
      label: `weekdag | ${day}`,
      defaultValues: { event_value: 0 - Index }
    });
    setDayToDisplay(Index);

    window.scrollTo(0, 0);
  };

  const { error, loading } = useQuery<{ programsByDate: IProgramsByDateGraphql[] }>(ProgramsByDateQuery, {
    variables: {
      date,
      programTypes: PROGRAM.EPISODE,
      sort: PROGRAM_SORT_KEY.AIREDDATETIME,
      availability: true
    },
    onCompleted: data => {
      if (!data.programsByDate[0]?.items) return;

      const missed = data.programsByDate[0].items
        .map(item => new EpisodeModel(item))
        .filter(item => item.isValid && !item.isLive);

      const oneEpisodePerSeries = filterVideosByUniqueSeriesGuid(missed);
      setPrograms(oneEpisodePerSeries);
    }
  });

  const { variables: featureVariables } = useFeature<string[]>(FEATURE_SLUG.POPULAR_MISSED_PAGE);

  useEffect(() => {
    if (!featureVariables) return;

    const ranking = [...featureVariables].reverse();
    const sorted = [...programs].sort((programA, programB) => {
      const posA = ranking.indexOf(programA.series.slug);
      const posB = ranking.indexOf(programB.series.slug);
      return posB - posA;
    });

    const result = sorted.reduce(
      (acc, program) => {
        if (ranking.includes(program.series.slug) && acc.favorites.length < maxAmount) {
          program.eventValues.label = popularTitle;
          program.eventValues.value = acc.favorites.length + 1;
          return { favorites: [...acc.favorites, program], sorted: acc.sorted };
        }
        program.eventValues.label = moreTitle;
        program.eventValues.value = acc.sorted.length + 1;
        return { favorites: acc.favorites, sorted: [...acc.sorted, program] };
      },
      { favorites: [], sorted: [] } as { favorites: VideoModel[]; sorted: VideoModel[] }
    );
    setSortedPrograms(result);
  }, [programs, featureVariables, maxAmount]);

  return (
    <>
      <Head>
        <title>{metadata?.title || META_TITLE_CONTENT.GEMIST}</title>
        <meta name="og:description" content={metadata?.description || META_DESCRIPTION_CONTENT.GEMIST} />
        <meta name="description" content={metadata?.description || META_DESCRIPTION_CONTENT.GEMIST} />
      </Head>
      <MissedStyle data-testid="missed">
        <ErrorBoundary onError={(_: any) => <CommonFallback message={FALLBACK_UI_LABELS.MISSED} />}>
          <MissedPickerStyle isHeaderHidden={isHeaderHidden}>
            <Picker items={SelectOptions} selectedItem={label} handlePickerItemClick={handleDayChange} />
          </MissedPickerStyle>
          {loading ? (
            <Grid gridSmRowGap="16px" gridMdRowGap="24px" gridLgRowGap="24px">
              <GridElement lgColumn="1 / 13" mdColumn="1 / 13" smColumn="1 / 13">
                <MissedListWrapperStyle>
                  <SkeletonMissedSection noOfItems={SKELETON_NUMBER_OF_POPULAR_ITEMS} />
                  <SkeletonMissedSection noOfItems={SKELETON_NUMBER_OF_OTHER_ITEMS} />
                </MissedListWrapperStyle>
              </GridElement>
            </Grid>
          ) : (
            <>
              {sortedPrograms?.favorites && sortedPrograms?.favorites?.length ? (
                <>
                  <Grid>
                    <GridElement lgColumn="1 / 13" mdColumn="1 / 13" smColumn="1 / 13">
                      <Heading2>{popularTitle}</Heading2>
                    </GridElement>
                  </Grid>
                  <Grid gridSmRowGap="16px" gridMdRowGap="24px" gridLgRowGap="24px">
                    <GridElement lgColumn="1 / 13" mdColumn="1 / 13" smColumn="1 / 13">
                      <MissedListWrapperStyle>
                        <VideoList smLandscape programList={sortedPrograms.favorites} />
                      </MissedListWrapperStyle>
                    </GridElement>
                  </Grid>
                  {sortedPrograms?.sorted?.length ? (
                    <Grid>
                      <GridElement lgColumn="1 / 13" mdColumn="1 / 13" smColumn="1 / 13">
                        <Heading2>{moreTitle}</Heading2>
                      </GridElement>
                    </Grid>
                  ) : null}
                </>
              ) : null}
              <Grid gridSmRowGap="16px" gridMdRowGap="24px" gridLgRowGap="24px">
                <GridElement lgColumn="1 / 13" mdColumn="1 / 13" smColumn="1 / 13">
                  <MissedListWrapperStyle data-testid="missedListWrapperStyle">
                    <ErrorBoundary onError={() => <CommonFallback message={FALLBACK_UI_LABELS.MISSED} />}>
                      {(() => {
                        if (error) {
                          return <ErrorMessage />;
                        }
                        if (!programs.length) {
                          const WatchListMessage = dynamic(() => import('components/WatchListMessage'));
                          return (
                            <WatchListMessage
                              isMissedPage
                              heading="Geen video’s beschikbaar"
                              message="Er zijn helaas (nog) geen video's beschikbaar. Probeer het later nog eens."
                            />
                          );
                        }
                        return <VideoList smLandscape programList={sortedPrograms?.sorted || programs} />;
                      })()}
                    </ErrorBoundary>
                  </MissedListWrapperStyle>
                </GridElement>
              </Grid>
            </>
          )}
        </ErrorBoundary>
      </MissedStyle>
    </>
  );
};

Missed.getInitialProps = async ({ apolloClient }: IGetInitialProps): Promise<any> => {
  if (!isServer) return;

  try {
    const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd');
    await apolloClient.query({
      query: ProgramsByDateQuery,
      variables: {
        date: yesterday,
        programTypes: PROGRAM.EPISODE
      }
    });
    const { data } = await apolloClient.query<{ overview: { metadata: IOverviewMetadata } }>({
      query: OverviewMetadataQuery,
      variables: {
        slug: MISSED_SLUG,
        client: PROMO_QUERY_CLIENT.DESKTOP
      }
    });
    return {
      initialMetadata: data.overview?.metadata
    };
  } catch (ignore) {}
};

export default Missed;
